import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { initiateDownloadZip } from "../../api";
import {
  usePrintingExport,
  usePrintList,
  useUpdatePrintStatus,
  useVendorList,
} from "../../hooks";
import { ActionButton } from "./ActionButton";
import { PrintingFilterPanel, PrintingFilters } from "./PrintingFilter";
import { StatusBadge } from "./StatusBadge";
import { logEvent } from "@shared/analytics/analytics";

interface CycleData {
  start_date: string;
  end_date: string;
  total_bill_count: number;
  cycle_name: string;
  cycle_id: number;
  run_id: number;
  printing_date: string;
  completion_date: string | null;
  vendor: any;
  lapsed_days: number;
  printing_status: string;
  file: string;
}

export function PrintingTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isVendorEditModalOpen, setIsVendorEditModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<CycleData | null>(null);
  const { toast } = useToast();
  const [vendor, setVendor] = useState<string>("");
  const [vendorError, setVendorError] = useState("");
  const { data: vendorlist } = useVendorList();

  // Parse filters from URL params
  const getFiltersFromUrl = (): PrintingFilters => {
    const urlFilters: PrintingFilters = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Parse status
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam;
    }

    // Parse vendor
    const vendorParam = searchParams.get("vendor");
    if (vendorParam && vendorParam !== "ALL_VENDORS") {
      urlFilters.vendor = vendorParam;
    }

    // Parse date range
    const startDateParam = searchParams.get("startDate");
    if (startDateParam) {
      urlFilters.startDate = startDateParam;
    }

    const endDateParam = searchParams.get("endDate");
    if (endDateParam) {
      urlFilters.endDate = endDateParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    // Parse sorting
    const sortFieldParam = searchParams.get("sort_field");
    if (sortFieldParam) {
      urlFilters.sort_field = sortFieldParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<PrintingFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    setFilters(getFiltersFromUrl());
  }, [searchParams, currentPage]);

  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setCurrentPage(parseInt(pageParam, 10));
    }
  }, [searchParams]);

  // Update sort config from URL
  useEffect(() => {
    const sortFieldParam = searchParams.get("sortField");
    const sortDirectionParam = searchParams.get("sortDirection");
    if (sortFieldParam && sortDirectionParam) {
      setSortConfig({
        field: sortFieldParam,
        direction: sortDirectionParam as "asc" | "desc",
      });
    }
  }, [searchParams]);

  const exportFilters = useMemo(() => {
    const exportParams = { ...filters };
    // Remove page and limit for export to get all data
    delete exportParams.page;
    delete exportParams.limit;
    return exportParams;
  }, [filters]);

  const { exportCSV, isExporting } = usePrintingExport(exportFilters);

  const { data: printingData } = usePrintList(filters);

  // Table columns configuration
  const printingColumns = [
    {
      key: "cycle_name",
      header: "Billing Cycle Name",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "billingPeriod",
      header: "Billing Period",
      render: (value: any, row: CycleData) => {
        return `${row.start_date} - ${row.end_date}`;
      },
    },
    {
      key: "total_bill_count",
      header: "Total Bills",
      sortable: true,
      headerClassName: "text-right",
    },
    {
      key: "printing_date",
      sortable: true,
      header: "Print Date",
      headerClassName: "text-center whitespace-nowrap",
    },
    {
      key: "completion_date",
      header: "Completion Date",
      sortable: true,
      headerClassName: "text-center whitespace-nowrap",
    },
    {
      key: "print_status",
      header: "Status",
      sortable: true,
      render: (value: any, row: any) => {
        return (
          <div>
            <StatusBadge
              status={
                row.printing_status === "Pending"
                  ? "pending"
                  : row.printing_status === "In Progress"
                  ? "Started"
                  : "completed"
              }
            />
          </div>
        );
      },
    },
    {
      key: "vendor",
      header: "Print By",
      headerClassName: "text-center whitespace-nowrap",
      render: (value: any) => (
        <div className="text-center">{value?.vendor_name || "NA"}</div>
      ),
    },
    {
      key: "lapsed_days",
      sortable: true,
      header: "Lapsed Days",
      headerClassName: "text-center whitespace-nowrap",
      render: (value: number) => (
        <div className="flex justify-center">
          <StatusBadge
            status={value > 7 ? "warning" : value > 3 ? "pending" : "info"}
            label={`${value ?? "NA"} days`}
          />
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      headerClassName: "text-center",
      render: (value: any, row: CycleData) => {
        return (
          <div
            className="flex justify-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionButton
              type="edit"
              onClick={() => openEditModal(row)}
              iconOnly
            />

            {row.printing_status === "Pending" && (
              <ActionButton
                type="mark-started"
                onClick={() => openVendorModal(row)}
                iconOnly
              />
            )}
            <ActionButton
              type="download"
              onClick={() => handleDownload(row.cycle_id)}
              iconOnly
            />

            {row.file !== "" && (
              <ActionButton
                type="print"
                onClick={() => handlePrint(row.file)}
                iconOnly
              />
            )}
          </div>
        );
      },
    },
  ];

  const handleApplyFilters = (newFilters: Partial<PrintingFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("vendor");
    params.delete("startDate");
    params.delete("endDate");
    params.delete("page");

    // Add new filter params
    if (newFilters.status) {
      params.set("status", newFilters.status);
    }
    if (newFilters.vendor) {
      params.set("vendor", newFilters.vendor);
    }
    if (newFilters.startDate) {
      params.set("startDate", newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set("endDate", newFilters.endDate);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("vendor");
    params.delete("startDate");
    params.delete("endDate");
    params.delete("page");
    params.delete("query");
    params.delete("sortField");
    params.delete("sortDirection");

    setCurrentPage(1);
    setSortConfig(null);
    setSearchParams(params);
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);

    const sortFieldValue = (direction === "asc" ? "+" : "-") + field;
    const params = new URLSearchParams(searchParams);
    params.set("sort_field", sortFieldValue); // use underscore key
    params.set("page", "1");
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
    setCurrentPage(newPage);
  };

  const handleSearch = (searchTerm: string, paramName: string) => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setCurrentPage(1);

    setSearchParams(params);
  };

  const handleExport = () => {
    exportCSV();
    logEvent("BX Printing Cycles Exported");
  };

  const updatePrintStatus = useUpdatePrintStatus();

  const parseFlexibleDateToString = (input) => {
    if (!input) return "";

    const knownFormats = [
      "YYYY-MM-DD",
      "DD MMM YYYY",
      "MMM DD YYYY",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)",
    ];

    for (const format of knownFormats) {
      const date = dayjs(input, format, true);
      if (date.isValid()) return date.format("YYYY-MM-DD");
    }

    const fallback = dayjs(input); // try native parse
    return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
  };

  const openVendorModal = (cycle: CycleData) => {
    setSelectedCycle(cycle);
    setIsVendorEditModalOpen(true);
  };
  const handleStatusChange = (cycle: CycleData, newStatus: string) => {
    updatePrintStatus.mutate(
      {
        runId: cycle.run_id,
        payload: {
          print_date: parseFlexibleDateToString(cycle.printing_date),
          print_status: newStatus,
        },
      },
      {
        onSuccess: async () => {
          logEvent(`BX Status Update`, {
            print_status: newStatus,
            runId: cycle.run_id,
          });
          await queryClient.invalidateQueries({
            queryKey:
              QueryKeyFactory.module.bx.printingDistribution.printList(),
          });
        },
      }
    );
    setIsEditModalOpen(false);
    toast({
      title: `Status updated`,
      description: `Cycle has been marked as ${
        newStatus === "printing" ? "started" : "completed"
      }`,
    });
  };

  const selectVendor = (cycle: CycleData, id: string | number) => {
    const selectedVendor = vendorlist.find((v) => v.value === id);
    const name = selectedVendor?.label;
    updatePrintStatus.mutate(
      {
        runId: cycle.run_id,
        payload: {
          vendor: {
            id: id,
            vendor_name: name,
          },
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              QueryKeyFactory.module.bx.printingDistribution.printList(),
          });
        },
      }
    );
    setIsVendorEditModalOpen(false);
    toast({
      title: `vendor updated`,
      description: "",
    });
  };

  const handlePrint = (file: any) => {
    const link = document.createElement("a");
    link.href = file;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = (cycleId: number) => {
    initiateDownloadZip(cycleId);
    toast({
      title: "Download In Progress...",
      description: "Please refresh after 5 minutes to download the zip",
    });
  };

  const openEditModal = (cycle: CycleData) => {
    setSelectedCycle(cycle);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <GenericTable
        data={printingData?.result ?? []}
        columns={printingColumns}
        rowKey="run_id"
        emptyMessage="No printing cycles found"
        searchPlaceholder="Search cycles..."
        searchParamName="query"
        searchDebounceMs={500}
        onSearch={handleSearch}
        sortConfig={sortConfig}
        onSort={handleSort}
        onExport={handleExport}
        filterPanel={
          <PrintingFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        pagination={{
          currentPage: currentPage,
          totalPages: Math.ceil((printingData?.count || 0) / PAGE_SIZE),
          totalItems: printingData?.count || 0,
          itemsPerPage: PAGE_SIZE,
          startItem: currentPage ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
          endItem: currentPage
            ? Math.min(currentPage * PAGE_SIZE, printingData?.count || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
      />

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Printing Status</DialogTitle>
            <DialogDescription>
              Update the status and details for {selectedCycle?.cycle_name}
            </DialogDescription>
          </DialogHeader>

          {selectedCycle && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="printDate" className="text-right text-sm">
                  Print Date
                </label>
                <div className="col-span-3">
                  <Input
                    value={selectedCycle.printing_date}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="status" className=" text-sm">
                  Status
                </label>
                <div className="col-span-3 flex gap-1">
                  <Button
                    disabled={
                      selectedCycle.printing_status === "In Progress" ||
                      selectedCycle.printing_status === "Completed"
                    }
                    variant={
                      selectedCycle.printing_status === "Pending"
                        ? "default"
                        : "outline"
                    }
                    onClick={() => handleStatusChange(selectedCycle, "Pending")}
                    className="flex-1"
                  >
                    Pending
                  </Button>
                  <Button
                    variant={
                      selectedCycle.printing_status === "In Progress"
                        ? "default"
                        : "outline"
                    }
                    disabled={selectedCycle.printing_status === "Completed"}
                    onClick={() =>
                      handleStatusChange(selectedCycle, "In Progress")
                    }
                    className="flex-1"
                  >
                    Started
                  </Button>
                  <Button
                    variant={
                      selectedCycle.printing_status === "Completed"
                        ? "default"
                        : "outline"
                    }
                    onClick={() =>
                      handleStatusChange(selectedCycle, "Completed")
                    }
                    className="flex-1"
                  >
                    Completed
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label className="text-right text-sm">Vendor</label>
                <div className="col-span-3">
                  <Input
                    value={selectedCycle.vendor?.vendor_name || "Unassigned"}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label className="text-right text-sm">Lapsed Days</label>
                <div className="col-span-3">
                  <Input
                    value={selectedCycle.lapsed_days + " days"}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isVendorEditModalOpen}
        onOpenChange={setIsVendorEditModalOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Accept Billing Cycle</DialogTitle>
            <DialogDescription>
              Assign this billing cycle to a vendor for printing
            </DialogDescription>
          </DialogHeader>

          {selectedCycle && (
            <>
              <Select
                value={vendor}
                onValueChange={(value) => {
                  setVendor(value);
                  setVendorError("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendorlist?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {vendorError && (
                <p className="text-sm text-red-500 mt-1">{vendorError}</p>
              )}
              <div className="mt-6 flex justify-end gap-4">
                <button
                  onClick={() => setIsVendorEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-black bg-white hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => selectVendor(selectedCycle, vendor)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-sky-500 hover:bg-sky-600"
                >
                  Accept & Start Printing
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
